import { ref } from 'vue'
import { apiFetch } from '@/services/api'

export interface DailyEmailStats {
  date: string
  sent: number
  delivered: number
  bounced: number
  replied_positive: number
  replied_negative: number
  unsubscribed: number
}

export interface BounceDetail {
  date: string
  email: string
  from_identity: string | null
  source: string
  reachout_id: string | null
  reachout_subject: string | null
  email_provider: string | null
  leadiq_status: string | null
}

export interface ProvenanceBreakdown {
  label: string
  bounced: number
  total_sent: number
  bounce_rate: number
}

export interface ReplyDetail {
  date: string
  email: string
  from_identity: string | null
  outcome: string // engaged | disqualified | pending
  reply_snippet: string
  contact_id: string | null
}

export interface UnsubscribeDetail {
  date: string
  email: string
  source: string
}

export interface EmailDeliverabilityResponse {
  total_sent: number
  total_delivered: number
  total_bounced: number
  total_replied: number
  total_unsubscribed: number
  replied_positive: number
  replied_negative: number
  delivery_rate: number
  bounce_rate: number
  reply_rate: number
  daily_stats: DailyEmailStats[]
  bounces: BounceDetail[]
  replies: ReplyDetail[]
  unsubscribes: UnsubscribeDetail[]
  mailboxes: string[]
  bounces_by_provider: ProvenanceBreakdown[]
  bounces_by_leadiq_status: ProvenanceBreakdown[]
}

export function useEmailDeliverability() {
  const data = ref<EmailDeliverabilityResponse | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  async function fetch(params: {
    startDate: string
    endDate: string
    salesPlayId?: string | null
    fromIdentity?: string | null
  }) {
    loading.value = true
    error.value = null
    try {
      const query = new URLSearchParams({
        start_date: params.startDate,
        end_date: params.endDate,
      })
      if (params.salesPlayId) {
        query.set('sales_play_id', params.salesPlayId)
      }
      if (params.fromIdentity) {
        query.set('from_identity', params.fromIdentity)
      }
      data.value = await apiFetch<EmailDeliverabilityResponse>(
        `/reports/email-deliverability?${query}`,
      )
    } catch (e: any) {
      error.value = e.message || 'Failed to load deliverability report'
    } finally {
      loading.value = false
    }
  }

  return { data, loading, error, fetch }
}
